/* ===== Domain Types — Core entities for Zetyar Nexo ===== */

import type { RoleName } from './roles'

export * from './roles'

// ─── Base Types ───────────────────────────────────────────
export type UUID = string
export type ISODateTime = string
export type ISODate = string

export interface Timestamps {
  readonly createdAt: ISODateTime
  readonly updatedAt: ISODateTime
}

export interface SoftDeletable {
  readonly deletedAt: ISODateTime | null
}

export interface PaginatedResult<T> {
  readonly data: T[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly totalPages: number
  readonly hasMore: boolean
}

export interface CursorPaginatedResult<T> {
  readonly data: T[]
  readonly nextCursor: string | null
  readonly hasMore: boolean
}

export interface Pagination {
  readonly page: number
  readonly pageSize: number
}

export interface CursorPagination {
  readonly cursor?: string
  readonly limit: number
}

// ─── Auth ─────────────────────────────────────────────────
export interface AuthUser {
  readonly uid: string
  readonly email: string
  readonly displayName: string | null
  readonly photoURL: string | null
  readonly emailVerified: boolean
}

export interface Profile extends Timestamps {
  readonly id: UUID
  readonly firebaseUid: string
  readonly email: string
  readonly displayName: string | null
  readonly avatarUrl: string | null
  readonly phone: string | null
  readonly rut?: string | null
  readonly emailVerified: boolean
  readonly isSuperAdmin: boolean
  readonly status: 'active' | 'suspended' | 'deleted'
  readonly lastLoginAt: ISODateTime | null
}

// ─── Organization ─────────────────────────────────────────
export type OrgModuleKey =
  | 'announcements'
  | 'members'
  | 'events'
  | 'projects'
  | 'chat'
  | 'documents'
  | 'campaigns'
  | 'finance'
  | 'agreements'
  | 'sales'
  | 'requests'
  | 'dues'

export interface ModuleDefinition {
  readonly key: OrgModuleKey
  readonly label: string
  readonly description: string
  readonly to: string
  readonly iconName: string
}

export const MODULE_DEFINITIONS: readonly ModuleDefinition[] = [
  {
    key: 'announcements',
    label: 'Comunicados',
    description: 'Publicación de avisos y comunicados oficiales',
    to: '/announcements',
    iconName: 'Megaphone',
  },
  {
    key: 'members',
    label: 'Miembros',
    description: 'Directorio y gestión de miembros del espacio',
    to: '/members',
    iconName: 'Users',
  },
  {
    key: 'dues',
    label: 'Cuotas',
    description: 'Gestión y pago de cuotas mensuales y membresías',
    to: '/dues',
    iconName: 'CreditCard',
  },
  {
    key: 'events',
    label: 'Actividades',
    description: 'Calendario de actividades y registro de asistentes',
    to: '/events',
    iconName: 'Calendar',
  },
  {
    key: 'projects',
    label: 'Proyectos',
    description: 'Seguimiento de proyectos y estado de avance',
    to: '/projects',
    iconName: 'Briefcase',
  },
  {
    key: 'chat',
    label: 'Chat',
    description: 'Canales de conversación y mensajes directos',
    to: '/chat',
    iconName: 'MessageSquare',
  },
  {
    key: 'documents',
    label: 'Documentos',
    description: 'Repositorio y consulta de archivos compartidos',
    to: '/documents',
    iconName: 'FileText',
  },
  {
    key: 'campaigns',
    label: 'Campañas',
    description: 'Campañas de recaudación de fondos y donaciones',
    to: '/campaigns',
    iconName: 'HeartHandshake',
  },
  {
    key: 'finance',
    label: 'Finanzas',
    description: 'Gestión y balances de ingresos y egresos',
    to: '/finance',
    iconName: 'DollarSign',
  },
  {
    key: 'agreements',
    label: 'Convenios',
    description: 'Alianzas y beneficios para los miembros',
    to: '/agreements',
    iconName: 'Handshake',
  },
  {
    key: 'sales',
    label: 'Ventas',
    description: 'Catálogo de artículos, insumos y reservas',
    to: '/sales',
    iconName: 'ShoppingCart',
  },
  {
    key: 'requests',
    label: 'Solicitudes',
    description: 'Recepción y seguimiento de solicitudes de socios',
    to: '/requests',
    iconName: 'ClipboardList',
  },
] as const

export function isModuleEnabled(
  organization: Organization | null | undefined,
  moduleKey: OrgModuleKey,
): boolean {
  if (!organization || !organization.features) return true
  return organization.features[moduleKey] !== false
}

export interface Organization extends Timestamps, SoftDeletable {
  readonly id: UUID
  readonly name: string
  readonly slug: string
  readonly description: string | null
  readonly logoUrl: string | null
  readonly logoDarkUrl?: string | null
  readonly faviconUrl?: string | null
  readonly brandColor?: string | null
  readonly primaryDomain?: string | null
  readonly features?: Record<string, boolean>
  readonly status: 'active' | 'suspended' | 'archived'
  readonly timezone: string
  readonly locale: string
  readonly createdBy: UUID
}

export interface OrganizationSettings extends Timestamps {
  readonly id: UUID
  readonly organizationId: UUID
  readonly currency: string
  readonly locale: string
  readonly timezone: string
  readonly maxMembers: number
  readonly maxStorageMb: number
  readonly features: Record<string, boolean>
}

export interface AgreementInfo extends Timestamps {
  readonly id: UUID
  readonly organizationId: UUID
  readonly title: string
  readonly description: string
  readonly discountDetails: string | null
  readonly partnerName: string
  readonly contactPhone: string | null
  readonly websiteUrl: string | null
}


// ─── Membership & Roles ──────────────────────────────────
export interface Membership extends Timestamps {
  readonly id: UUID
  readonly organizationId: UUID
  readonly userId: UUID
  readonly roles: RoleName[]
  readonly status: 'active' | 'inactive' | 'suspended'
  readonly joinedAt: ISODateTime
  // Populated fields
  readonly roleAssignments?: { readonly id: UUID; readonly role: RoleName }[]
  readonly profile?: Profile
  readonly organization?: Organization
  // Membership-scoped display info (memberships.display_name etc - how this person is shown
  // within this organization specifically, editable via update_membership_details).
  readonly displayName?: string
  readonly institutionalEmail?: string | null
  readonly position?: string | null
  readonly department?: string | null
  readonly memberNumber?: string | null
}

export interface Invitation {
  readonly id: UUID
  readonly organizationId: UUID
  readonly email: string
  readonly role: RoleName
  readonly invitedBy: UUID | null
  readonly token: string
  readonly status: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled'
  readonly expiresAt: ISODateTime
  readonly acceptedAt: ISODateTime | null
  readonly createdAt: ISODateTime
  // Populated
  readonly organization?: Organization
}

// ─── Dues / Cuotas Sociales ──────────────────────────────
// Server-side state lives in organization_dues_settings, due_payments and membership_dues
// (supabase/migrations/0033_membership_dues.sql). Payment tokens never belong here.

/** Month in 'YYYY-MM' form, e.g. '2026-09'. */
export type DuePeriod = string
export type ManualDuePaymentMethod = 'transfer' | 'cash' | 'other'
export type DuePaymentMethod = ManualDuePaymentMethod | 'mercadopago'
export type MembershipDueStatus = 'paid' | 'exempt' | 'processing'

export const MAX_DUE_MONTHS_PER_PAYMENT = 24

export interface OrgDuesSettings {
  readonly organizationId: UUID
  readonly monthlyAmount: number
  readonly currency: string
  readonly dueDay: number
  readonly bankName: string | null
  readonly accountType: string | null
  readonly accountNumber: string | null
  readonly accountHolder: string | null
  readonly accountRut: string | null
  readonly accountEmail: string | null
  readonly instructions: string | null
  readonly onlinePaymentEnabled: boolean
  readonly updatedAt: ISODateTime
}

export interface MembershipDue {
  readonly id: UUID
  readonly membershipId: UUID
  readonly period: DuePeriod
  readonly status: MembershipDueStatus
  readonly amount: number
  readonly currency: string
  readonly duePaymentId: UUID | null
  readonly paymentMethod: DuePaymentMethod | null
  readonly paidAt: ISODateTime | null
  readonly notes: string | null
}

export interface DuesRosterEntry {
  readonly membershipId: UUID
  readonly displayName: string
  readonly memberNumber: string | null
  readonly position: string | null
  readonly institutionalEmail: string | null
  readonly dueId: UUID | null
  /** null means the month is pending for this member. */
  readonly dueStatus: MembershipDueStatus | null
  readonly duePaymentId: UUID | null
  readonly paymentMethod: DuePaymentMethod | null
  readonly paidAt: ISODateTime | null
  readonly amount: number | null
}

export interface DuesPeriodSummary {
  readonly activeMembers: number
  readonly paidCount: number
  readonly exemptCount: number
  readonly processingCount: number
  readonly pendingCount: number
  readonly collectedAmount: number
}

const DUE_PERIOD_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/

export function isDuePeriod(value: string): value is DuePeriod {
  return DUE_PERIOD_PATTERN.test(value)
}

export function periodFromDate(date: Date): DuePeriod {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

/** Assumes `period` already passed isDuePeriod. */
function parsePeriod(period: DuePeriod): { readonly year: number; readonly month: number } {
  return { year: Number(period.slice(0, 4)), month: Number(period.slice(5, 7)) }
}

export function addPeriods(period: DuePeriod, offset: number): DuePeriod {
  if (!isDuePeriod(period)) throw new Error(`Invalid period: ${period}`)
  const { year, month } = parsePeriod(period)
  const totalMonths = year * 12 + (month - 1) + offset
  const nextYear = Math.floor(totalMonths / 12)
  const nextMonth = totalMonths - nextYear * 12 + 1
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}`
}

/** Consecutive months starting at `start`, mirroring app.dues_periods in SQL. */
export function periodRange(start: DuePeriod, months: number): DuePeriod[] {
  if (!Number.isInteger(months) || months < 1 || months > MAX_DUE_MONTHS_PER_PAYMENT) {
    throw new Error(`Months must be an integer between 1 and ${MAX_DUE_MONTHS_PER_PAYMENT}`)
  }
  return Array.from({ length: months }, (_, index) => addPeriods(start, index))
}

export function formatPeriodLabel(period: DuePeriod, locale = 'es-CL'): string {
  if (!isDuePeriod(period)) return period
  const { year, month } = parsePeriod(period)
  const label = new Date(year, month - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

// ─── Announcements ────────────────────────────────────────
export interface Announcement extends Timestamps, SoftDeletable {
  readonly id: UUID
  readonly organizationId: UUID
  readonly title: string
  readonly content: string
  readonly summary: string | null
  readonly isPinned: boolean
  readonly isDraft: boolean
  readonly scheduledAt: ISODateTime | null
  readonly publishedAt: ISODateTime | null
  readonly audience: { type: 'all' | 'roles'; roles?: string[] }
  readonly attachments: FileAttachment[]
  readonly createdBy: UUID
  readonly updatedBy: UUID | null
  // Populated
  readonly author?: Profile
  readonly isRead?: boolean
  readonly readCount?: number
}

export interface FileAttachment {
  readonly id: UUID
  readonly fileName: string
  readonly fileType: string
  readonly fileSize: number
  readonly storagePath: string
  readonly url?: string
}

// ─── Projects ─────────────────────────────────────────────
export type ProjectStatus = 'draft' | 'planned' | 'active' | 'paused' | 'completed' | 'cancelled'
export type ProjectPriority = 'low' | 'medium' | 'high' | 'critical'

export interface Project extends Timestamps, SoftDeletable {
  readonly id: UUID
  readonly organizationId: UUID
  readonly title: string
  readonly description: string | null
  readonly status: ProjectStatus
  readonly priority: ProjectPriority
  readonly progress: number
  readonly responsibleId: UUID | null
  readonly startDate: ISODate | null
  readonly targetDate: ISODate | null
  readonly completedAt: ISODateTime | null
  readonly createdBy: UUID
  // Populated
  readonly responsible?: Profile
  readonly members?: ProjectMember[]
  readonly updatesCount?: number
}

export interface ProjectMember {
  readonly id: UUID
  readonly projectId: UUID
  readonly userId: UUID
  readonly role: 'lead' | 'member' | 'observer'
  readonly joinedAt: ISODateTime
  readonly profile?: Profile
}

export interface ProjectUpdate {
  readonly id: UUID
  readonly projectId: UUID
  readonly organizationId: UUID
  readonly content: string
  readonly progressChange: number | null
  readonly statusChange: ProjectStatus | null
  readonly attachments: FileAttachment[]
  readonly createdBy: UUID
  readonly createdAt: ISODateTime
  readonly author?: Profile
}

// ─── Chat ─────────────────────────────────────────────────
export type ChannelType = 'general' | 'public' | 'private'

export interface Channel extends Timestamps, SoftDeletable {
  readonly id: UUID
  readonly organizationId: UUID
  readonly name: string
  readonly description: string | null
  readonly type: ChannelType
  readonly createdBy: UUID
  // Populated
  readonly memberCount?: number
  readonly unreadCount?: number
  readonly lastMessage?: Message
}

export interface ChannelMember {
  readonly id: UUID
  readonly channelId: UUID
  readonly userId: UUID
  readonly lastReadAt: ISODateTime
  readonly notificationsEnabled: boolean
  readonly joinedAt: ISODateTime
  readonly profile?: Profile
}

export interface Message extends SoftDeletable {
  readonly id: UUID
  readonly channelId: UUID
  readonly organizationId: UUID
  readonly senderId: UUID
  readonly content: string
  readonly replyToId: UUID | null
  readonly isEdited: boolean
  readonly editedAt: ISODateTime | null
  readonly createdAt: ISODateTime
  // Populated
  readonly sender?: Profile
  readonly replyTo?: Message
  readonly attachments?: MessageAttachment[]
}

export interface MessageAttachment {
  readonly id: UUID
  readonly messageId: UUID
  readonly fileName: string
  readonly fileType: string
  readonly fileSize: number
  readonly storagePath: string
  readonly url?: string
}

// ─── Documents ────────────────────────────────────────────
export interface DocumentCategory {
  readonly id: UUID
  readonly organizationId: UUID
  readonly name: string
  readonly description: string | null
  readonly parentId: UUID | null
  readonly sortOrder: number
  readonly createdAt: ISODateTime
  readonly children?: DocumentCategory[]
}

/** Activity photo: stored twice (full + thumbnail), both compressed in the browser. */
export interface EventPhoto {
  readonly id: UUID
  readonly organizationId: UUID
  readonly eventId: UUID
  readonly storagePath: string
  readonly thumbPath: string
  readonly byteSize: number
  readonly thumbByteSize: number
  readonly width: number
  readonly height: number
  readonly caption: string | null
  readonly uploadedByMembershipId: UUID
  readonly createdAt: ISODateTime
}

/**
 * Digitised handwritten signature (firma electronica simple, Ley 19.799); never firma avanzada.
 * See supabase/migrations/0038_document_signatures.sql.
 */
export type DocumentSignatureMethod = 'canvas' | 'image'

export interface DocumentSignature {
  readonly id: UUID
  readonly organizationId: UUID
  readonly documentId: UUID
  readonly membershipId: UUID
  readonly method: DocumentSignatureMethod
  readonly signerName: string
  readonly signerPosition: string | null
  /** SHA-256 of the stored file, to check a copy later. */
  readonly fileSha256: string
  readonly signedAt: ISODateTime
}

export interface Document extends Timestamps, SoftDeletable {
  readonly id: UUID
  readonly organizationId: UUID
  readonly categoryId: UUID | null
  readonly name: string
  readonly description: string | null
  readonly fileName: string
  readonly fileType: string
  readonly fileSize: number
  readonly storagePath: string
  readonly version: number
  readonly parentId: UUID | null
  readonly downloadCount: number
  readonly uploadedBy: UUID
  // Populated
  readonly uploader?: Profile
  readonly category?: DocumentCategory
}

// ─── Campaigns ────────────────────────────────────────────
export type CampaignStatus = 'draft' | 'active' | 'paused' | 'completed' | 'cancelled'
export type TransactionStatus = 'pending' | 'authorized' | 'paid' | 'rejected' | 'cancelled' | 'refunded' | 'expired'

export interface Campaign extends Timestamps, SoftDeletable {
  readonly id: UUID
  readonly organizationId: UUID
  readonly title: string
  readonly description: string
  readonly imageUrl: string | null
  readonly goalAmount: number
  readonly raisedAmount: number
  readonly contributorCount: number
  readonly currency: string
  readonly status: CampaignStatus
  readonly visibility: 'members' | 'public'
  readonly startDate: ISODateTime | null
  readonly endDate: ISODateTime | null
  readonly createdBy: UUID
  // Populated
  readonly author?: Profile
  readonly progressPercentage?: number
}

export interface Contribution {
  readonly id: UUID
  readonly campaignId: UUID
  readonly organizationId: UUID
  readonly userId: UUID | null
  readonly amount: number
  readonly currency: string
  readonly isAnonymous: boolean
  readonly message: string | null
  readonly transactionId: UUID | null
  readonly status: 'pending' | 'paid' | 'cancelled' | 'refunded'
  readonly createdAt: ISODateTime
  readonly contributor?: Profile
}

export interface PaymentTransaction extends Timestamps {
  readonly id: UUID
  readonly organizationId: UUID
  readonly externalId: string | null
  readonly provider: string
  readonly type: 'campaign_contribution' | 'supply_purchase' | 'subscription'
  readonly amount: number
  readonly currency: string
  readonly status: TransactionStatus
  readonly idempotencyKey: string
  readonly metadata: Record<string, unknown>
  readonly userId: UUID
}

// ─── Finance ──────────────────────────────────────────────
export interface FinanceCategory {
  readonly id: UUID
  readonly organizationId: UUID
  readonly name: string
  readonly description: string | null
  readonly color: string
  readonly isActive: boolean
  readonly sortOrder: number
  readonly createdAt: ISODateTime
}

export interface IncomeRecord extends Timestamps, SoftDeletable {
  readonly id: UUID
  readonly organizationId: UUID
  readonly categoryId: UUID
  readonly amount: number
  readonly currency: string
  readonly description: string
  readonly referenceNumber: string | null
  readonly receiptUrl: string | null
  readonly source: string | null
  readonly date: ISODate
  readonly createdBy: UUID
  // Populated
  readonly category?: FinanceCategory
  readonly author?: Profile
}

export interface ExpenseRecord extends Timestamps, SoftDeletable {
  readonly id: UUID
  readonly organizationId: UUID
  readonly categoryId: UUID
  readonly amount: number
  readonly currency: string
  readonly description: string
  readonly referenceNumber: string | null
  readonly receiptUrl: string | null
  readonly vendor: string | null
  readonly date: ISODate
  readonly status: 'pending' | 'approved' | 'rejected'
  readonly approvedBy: UUID | null
  readonly approvedAt: ISODateTime | null
  readonly createdBy: UUID
  // Populated
  readonly category?: FinanceCategory
  readonly author?: Profile
  readonly approver?: Profile
}

export interface FinancePeriod {
  readonly id: UUID
  readonly organizationId: UUID
  readonly name: string
  readonly type: 'monthly' | 'quarterly' | 'annual' | 'custom'
  readonly startDate: ISODate
  readonly endDate: ISODate
  readonly isClosed: boolean
  readonly totalIncome: number
  readonly totalExpenses: number
  readonly balance: number
  readonly createdAt: ISODateTime
}

export interface FinanceBalance {
  readonly totalIncome: number
  readonly totalExpenses: number
  readonly balance: number
  readonly incomeByCategory: Array<{ category: FinanceCategory; total: number }>
  readonly expensesByCategory: Array<{ category: FinanceCategory; total: number }>
}

// ─── Supplies & Reservations ──────────────────────────────
export interface SupplyProduct extends Timestamps, SoftDeletable {
  readonly id: UUID
  readonly organizationId: UUID
  readonly name: string
  readonly description: string | null
  readonly imageUrl: string | null
  readonly unit: string
  readonly isActive: boolean
  readonly createdBy: UUID
}

export type OfferingStatus = 'draft' | 'active' | 'paused' | 'closed' | 'cancelled'
export type ReservationStatus = 'pending' | 'paid' | 'confirmed' | 'delivered' | 'cancelled'

export interface SupplyOffering extends Timestamps {
  readonly id: UUID
  readonly organizationId: UUID
  readonly productId: UUID
  readonly title: string
  readonly description: string | null
  readonly unitPrice: number
  readonly currency: string
  readonly totalStock: number
  readonly remainingStock: number
  readonly maxPerUser: number
  readonly status: OfferingStatus
  readonly startDate: ISODateTime
  readonly endDate: ISODateTime
  readonly createdBy: UUID
  // Populated
  readonly product?: SupplyProduct
  readonly reservationCount?: number
}

export interface SupplyReservation extends Timestamps {
  readonly id: UUID
  readonly organizationId: UUID
  readonly offeringId: UUID
  readonly userId: UUID
  readonly quantity: number
  readonly unitPrice: number
  readonly totalAmount: number
  readonly currency: string
  readonly status: ReservationStatus
  readonly transactionId: UUID | null
  readonly deliveredAt: ISODateTime | null
  readonly deliveredBy: UUID | null
  readonly notes: string | null
  // Populated
  readonly offering?: SupplyOffering
  readonly user?: Profile
}

// ─── Service Requests (Solicitudes) ───────────────────────
export type ServiceRequestStatus = 'open' | 'in_progress' | 'closed'

export interface ServiceRequest extends Timestamps {
  readonly id: UUID
  readonly organizationId: UUID
  readonly requestedById: UUID
  readonly subject: string
  readonly description: string
  readonly category: string | null
  readonly status: ServiceRequestStatus
  readonly assignedToId: UUID | null
  readonly closedAt: ISODateTime | null
  // Populated
  readonly requestedByDisplayName?: string
  readonly assignedToDisplayName?: string | null
}

export interface ServiceRequestComment {
  readonly id: UUID
  readonly requestId: UUID
  readonly authorId: UUID
  readonly content: string
  readonly createdAt: ISODateTime
  // Populated
  readonly authorDisplayName?: string
}

// ─── Notifications ────────────────────────────────────────
export interface Notification {
  readonly id: UUID
  readonly organizationId: UUID
  readonly membershipId: UUID
  readonly type: string
  readonly title: string
  readonly body: string | null
  readonly targetType: string | null
  readonly targetId: UUID | null
  readonly isRead: boolean
  readonly createdAt: ISODateTime
}

export interface NotificationPreferences {
  readonly id: UUID
  readonly userId: UUID
  readonly organizationId: UUID
  readonly announcements: boolean
  readonly messages: boolean
  readonly projects: boolean
  readonly campaigns: boolean
  readonly documents: boolean
  readonly supplies: boolean
  readonly finance: boolean
  readonly pushEnabled: boolean
  readonly emailEnabled: boolean
}

// ─── Events ───────────────────────────────────────────────
export interface CalendarEvent extends Timestamps, SoftDeletable {
  readonly id: UUID
  readonly organizationId: UUID
  readonly title: string
  readonly description: string | null
  readonly location: string | null
  readonly isVirtual: boolean
  readonly virtualUrl: string | null
  readonly startDate: ISODateTime
  readonly endDate: ISODateTime
  readonly capacity: number | null
  readonly status: string
  readonly organizerId: UUID
  readonly organizer?: Profile
  readonly isRegistered?: boolean
}

export interface EventRegistration extends Timestamps {
  readonly id: UUID
  readonly eventId: UUID
  readonly membershipId: UUID
  readonly status: 'registered' | 'attended' | 'cancelled'
  readonly qrToken: string
  readonly checkedInAt: ISODateTime | null
}

// ─── Audit ────────────────────────────────────────────────
export interface AuditLog {
  readonly id: UUID
  readonly organizationId: UUID
  readonly actorMembershipId: UUID | null
  readonly action: string
  readonly targetType: string
  readonly targetId: UUID | null
  readonly detail: string | null
  readonly createdAt: ISODateTime
  // Populated
  readonly actorDisplayName?: string | null
}

// ─── Subscription Plans ───────────────────────────────────
export interface SubscriptionPlan extends Timestamps {
  readonly id: UUID
  readonly name: 'starter' | 'professional' | 'enterprise'
  readonly displayName: string
  readonly description: string | null
  readonly maxMembers: number
  readonly maxStorageMb: number
  readonly maxProjects: number
  readonly maxChannels: number
  readonly features: Record<string, boolean>
  readonly priceMonthly: number
  readonly priceYearly: number
  readonly currency: string
  readonly isActive: boolean
  readonly sortOrder: number
}

export interface OrganizationSubscription extends Timestamps {
  readonly id: UUID
  readonly organizationId: UUID
  readonly planId: UUID
  readonly status: 'active' | 'past_due' | 'cancelled' | 'trial'
  readonly currentPeriodStart: ISODateTime
  readonly currentPeriodEnd: ISODateTime
  readonly assignedBy: UUID | null
  // Populated
  readonly plan?: SubscriptionPlan
}

// ─── Feature Flags ────────────────────────────────────────
export interface FeatureFlag extends Timestamps {
  readonly id: UUID
  readonly name: string
  readonly description: string | null
  readonly isEnabled: boolean
  readonly organizationIds: UUID[]
}

// ─── App Context ──────────────────────────────────────────
export interface AppContext {
  readonly user: AuthUser | null
  readonly profile: Profile | null
  readonly currentOrganization: Organization | null
  readonly currentMembership: Membership | null
  readonly permissions: string[]
  readonly organizations: Organization[]
}

// ─── Supply POS Configuration ──────────────────────────────
export interface SupplyConfig {
  readonly organizationId: UUID
  readonly isLimitActive: boolean
  readonly maxMonthlyQty: number
  readonly unitLabel: string
  readonly description: string
}
